// GeneratorsCard — Phase 4 of the Site Overview redesign (djin-cloud#67).
//
// Row 4 of the dashboard grid (reordered per #273; was row 3 pre-2026-05-11).
// Full-width (col-span-12 on all breakpoints).
// Renders a dense row list of generators: one row per device, with an
// ON/OFF pill and an inline 5-tile strip (kW, kVAR, kVA, Load %, Fuel MMBtu/h).
// A footer carries the site-level fuel total (passthrough) and a client-side
// sum of active power across rows.
//
// Design contract: docs/plans/2026-04-20-site-overview-redesign-design.md §4.3.
// Mockup: docs/plans/2026-04-20-site-overview-mockup.html (row 3 in mockup; row 4 after #273).
//
// Data contract — driven entirely off the WebSocket `telemetry:batch` payload:
//   - `generators` (array)     → from site_summary.generators
//   - `generators_total_count` → from site_summary
//   - `generators_online_count`→ from site_summary
//   - `fuel_consumption_mmbtu_h` (footer total) → from kpiValues
//
// Null-safety rules (workspace rule [No fabricated fallback data]):
//   - `generators === null`      → unknown roster; render "Roster not yet loaded" row.
//   - `generators === []` + total=0 → no generators at this site.
//   - `generators === []` + total>0 → known roster, awaiting first sample.
//   - row-level nulls render as "—" (no `?? 0` defaults).
//
// MVP behavior:
//   - `label` is always undefined on the wire for MVP. The conditional is coded
//     now so a Phase 6+ display-name rollout doesn't need another PR touching
//     this card. Design §9.3.
//   - "+N more" overflow link is text-only (no click handler). The generator
//     detail drawer is tracked for Phase 6+.

import type { GeneratorRow } from '../../../types/project.types';

export interface GeneratorsCardProps {
  /** Live site_summary values: generators_online_count, generators_total_count, fuel_consumption_mmbtu_h. */
  kpiValues: Record<string, number | null>;
  /** From site_summary.generators (WebSocket wire). null = unknown roster, [] = known-zero or awaiting data. */
  generators: GeneratorRow[] | null;
}

const MAX_ROWS = 8;

function formatNumber(val: number | null, fractionDigits = 1): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

function formatInt(val: number | null): string {
  if (val == null) return '—';
  return val.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function formatCount(online: number | null, total: number | null): string {
  if (online == null || total == null) return '— / —';
  return `${online} / ${total}`;
}

export function GeneratorsCard({ kpiValues, generators }: GeneratorsCardProps) {
  const onlineCount = kpiValues.generators_online_count ?? null;
  const totalCount = kpiValues.generators_total_count ?? null;
  const fuelTotal = kpiValues.fuel_consumption_mmbtu_h ?? null;

  return (
    <section
      data-testid="generators-card"
      className="card rounded-xl border border-ems-border-bright/50 overflow-hidden bg-ems-bg-card/60 flex flex-col"
    >
      <div className="px-5 py-3 flex items-center gap-2 border-b border-ems-border">
        <h3 className="text-sm font-semibold text-ems-text-primary tracking-wide uppercase">
          Generators
        </h3>
        <span
          data-testid="generators-count"
          className="text-[11px] text-ems-text-muted tabular-nums"
        >
          {formatCount(onlineCount, totalCount)} online
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full bg-ems-green animate-ems-pulse"
            aria-hidden="true"
          />
          <span className="text-[10px] text-ems-text-muted uppercase tracking-wider">Live</span>
        </div>
      </div>

      <div className="p-4 flex flex-col gap-2">
        <GeneratorsBody
          generators={generators}
          totalCount={totalCount}
        />

        {/* Footer — site-level totals. Always render even in empty-state branches so
            the wire-level fuel_consumption_mmbtu_h stays visible regardless of the
            per-device fan-out state. */}
        <div className="mt-2 pt-3 border-t border-ems-border flex items-center justify-between text-[11px] text-ems-text-muted">
          <span>
            <span className="uppercase tracking-wider">Total fuel:</span>{' '}
            <span
              data-testid="generators-total-fuel"
              className="font-mono tabular-nums text-ems-text-primary"
            >
              {formatNumber(fuelTotal)}
            </span>{' '}
            MMBtu/h
          </span>
          <span>
            <span className="uppercase tracking-wider">Σ Active Power (this batch):</span>{' '}
            <span
              data-testid="generators-sum-kw"
              className="font-mono tabular-nums text-ems-text-primary"
            >
              {formatNumber(sumKw(generators))}
            </span>{' '}
            kW
          </span>
        </div>
      </div>
    </section>
  );
}

function sumKw(generators: GeneratorRow[] | null): number | null {
  if (!generators || generators.length === 0) return null;
  let sum = 0;
  let anyValue = false;
  for (const g of generators) {
    if (g.kw != null) {
      sum += g.kw;
      anyValue = true;
    }
  }
  return anyValue ? sum : null;
}

function GeneratorsBody({
  generators,
  totalCount,
}: {
  generators: GeneratorRow[] | null;
  totalCount: number | null;
}) {
  if (generators === null) {
    return (
      <div
        data-testid="generators-empty-unknown"
        className="text-[11px] text-ems-text-muted py-4 text-center"
      >
        Roster not yet loaded — awaiting first sample
      </div>
    );
  }

  if (generators.length === 0 && totalCount === 0) {
    return (
      <div
        data-testid="generators-empty-zero"
        className="text-[11px] text-ems-text-muted py-4 text-center"
      >
        No generators at this site
      </div>
    );
  }

  if (generators.length === 0 && totalCount != null && totalCount > 0) {
    return (
      <div
        data-testid="generators-empty-awaiting"
        className="text-[11px] text-ems-text-muted py-4 text-center"
      >
        Awaiting first sample from {totalCount} generator{totalCount === 1 ? '' : 's'}
      </div>
    );
  }

  // Defensive fallback: generators=[] with null totalCount (shouldn't happen
  // because null roster → null array, but render awaiting-state if it does).
  if (generators.length === 0) {
    return (
      <div
        data-testid="generators-empty-awaiting"
        className="text-[11px] text-ems-text-muted py-4 text-center"
      >
        Awaiting first sample
      </div>
    );
  }

  const visible = generators.slice(0, MAX_ROWS);
  const overflow = generators.length - MAX_ROWS;

  return (
    <>
      <div className="flex flex-col divide-y divide-ems-border/60">
        {visible.map((g) => (
          <GeneratorListRow key={g.device_id} row={g} />
        ))}
      </div>
      {overflow > 0 && (
        <div
          data-testid="generators-overflow"
          className="pt-2 text-[11px] text-ems-text-muted italic text-center"
          title="Generator detail drawer is a future phase"
        >
          +{overflow} more
        </div>
      )}
    </>
  );
}

/**
 * Threshold (kW) above which an online generator is treated as RUNNING on the
 * strength of its power output alone. A real running generator produces well
 * above this; idling / parasitic load sits near zero. See GEN-2934 at Hondo
 * (0 kW, online) vs GEN-3255..3270 (~285 kW, online) — a 1 kW threshold
 * separates them cleanly.
 */
const RUNNING_POWER_THRESHOLD_KW = 1;

/**
 * Three-state generator pill: RUNNING / STANDBY / OFFLINE.
 *
 * Precedence (djin-cloud#328):
 *   - not online                                 → OFFLINE (muted dot)
 *   - online AND breaker_closed === true         → RUNNING (green dot)
 *   - online AND kw > RUNNING_POWER_THRESHOLD_KW  → RUNNING (green dot)
 *   - online, otherwise                          → STANDBY (amber dot)
 *
 * Power overrides the breaker signal. genieos serializes `breaker_closed` as
 * a plain bool (never null), and that field has been observed to read `false`
 * for generators the genieos local UI shows as running with breaker CLOSED —
 * so trusting `breaker_closed === false` outright mislabelled loaded
 * generators STANDBY (#328, Turner: 4 gens at ~117 kW each). The kw check now
 * runs ahead of any STANDBY verdict. `breaker_closed === true` still wins
 * first so a synchronised generator idling at zero load reads RUNNING.
 */
function genState(row: GeneratorRow): { label: 'RUNNING' | 'STANDBY' | 'OFFLINE'; dot: string; text: string } {
  if (!row.online) return { label: 'OFFLINE', dot: 'bg-ems-text-muted/50', text: 'text-ems-text-muted' };
  if (row.breaker_closed === true) return { label: 'RUNNING', dot: 'bg-ems-green', text: 'text-ems-green' };
  // Power overrides breaker_closed=false/null: a generator clearly producing
  // power is RUNNING regardless of what the (possibly stale) breaker signal
  // reports. See djin-cloud#328.
  if (row.kw != null && row.kw > RUNNING_POWER_THRESHOLD_KW) {
    return { label: 'RUNNING', dot: 'bg-ems-green', text: 'text-ems-green' };
  }
  return { label: 'STANDBY', dot: 'bg-ems-amber', text: 'text-ems-amber' };
}

function GeneratorListRow({ row }: { row: GeneratorRow }) {
  const hasLabel = row.label != null && row.label.length > 0;
  const state = genState(row);

  return (
    <div
      data-testid={`generator-row-${row.device_id}`}
      className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2"
    >
      {/* Device id + optional label */}
      <div className="min-w-[8rem] flex-shrink-0">
        {hasLabel ? (
          <>
            <div className="text-sm font-medium text-ems-text-primary truncate">{row.label}</div>
            <div className="text-[10px] text-ems-text-muted font-mono">{row.device_id}</div>
          </>
        ) : (
          <div
            data-testid={`generator-device-id-${row.device_id}`}
            className="text-sm font-mono text-ems-text-primary"
          >
            {row.device_id}
          </div>
        )}
      </div>

      {/* State dot + RUNNING/STANDBY/OFFLINE text label (a11y: don't rely on color alone). */}
      <div className="flex items-center gap-1.5 flex-shrink-0" title={state.label}>
        <span
          data-testid={`generator-online-dot-${row.device_id}`}
          className={`w-2 h-2 rounded-full ${state.dot}`}
          aria-hidden="true"
        />
        <span
          data-testid={`generator-online-label-${row.device_id}`}
          className={`text-[10px] font-semibold tracking-wider ${state.text}`}
        >
          {state.label}
        </span>
      </div>

      {/* Inline metric strip — 3 cols on mobile (5th tile wraps), 5 cols on md+ */}
      <div className="w-full md:ml-auto md:w-auto md:flex-1 grid grid-cols-3 md:grid-cols-5 gap-2 max-w-[40rem]">
        <InlineTile label="kW" value={formatNumber(row.kw)} testId={`generator-kw-${row.device_id}`} />
        <InlineTile label="kVAR" value={formatNumber(row.kvar)} testId={`generator-kvar-${row.device_id}`} />
        <InlineTile label="kVA" value={formatNumber(row.kva)} testId={`generator-kva-${row.device_id}`} />
        <InlineTile
          label="Load %"
          value={formatInt(row.engine_load_pct)}
          testId={`generator-load-${row.device_id}`}
        />
        <InlineTile
          label="Fuel MMBtu/h"
          value={formatNumber(row.fuel_rate_mmbtu_h)}
          testId={`generator-fuel-${row.device_id}`}
        />
      </div>
    </div>
  );
}

function InlineTile({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="rounded-md px-2 py-1 bg-ems-bg-card/40 border border-ems-border">
      <div className="text-[9px] text-ems-text-muted uppercase tracking-wider">{label}</div>
      <div
        data-testid={testId}
        className="text-sm font-mono font-semibold tabular-nums text-ems-text-primary"
      >
        {value}
      </div>
    </div>
  );
}

export default GeneratorsCard;
